import {User} from '@/domain/models/User';

type UserAvatars = Pick<User, 'avatars' | 'avatar_urls'>;

/**
 * Keeps the previous avatar ordering when the underlying set of avatars
 * hasn't changed, preventing the "best avatar" from flickering on re-fetch.
 */
export function stabilizeAvatars(
  updated: UserAvatars,
  previous?: UserAvatars
): UserAvatars {
  if (!previous) {
    return updated;
  }

  if (hasSameAvatarIds(previous.avatars, updated.avatars)) {
    return {
      avatars: previous.avatars,
      avatar_urls: previous.avatar_urls,
    };
  }

  return {
    avatars: updated.avatars,
    avatar_urls: updated.avatar_urls,
  };
}

export function withStableAvatars<T extends UserAvatars, K extends UserAvatars>(
  updated: T,
  previous?: K
): T {
  return {...updated, ...stabilizeAvatars(updated, previous)};
}

function hasSameAvatarIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const set = new Set(a);
  return b.every((id) => set.has(id));
}
